// SwitcherToggle.jsx

import React, { useMemo, useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';
import { useWorkspace } from 'context/WorkspaceContext';

// Utilities
import { PhotoShape } from 'utilities/Enums';

// CSS
import './SwitcherToggle.css';

// Icons
import Icon from 'svg/Icon';

// Components
import InviteModal from 'main/sidebar/switcher/popup/members/invite/InviteModal';
import Popup from 'components/popup/Popup';
import SettingsModal from 'main/sidebar/switcher/popup/settings/SettingsModal';
import SwitcherPopup from 'main/sidebar/switcher/popup/SwitcherPopup';
import WorkspaceCover from 'components/workspacecover/WorkspaceCover';

const SETTINGS_TAB_PLAN = 'plan';

/**
 * SwitcherToggle
 * 
 * Renders the toggle component for the sidebar switcher.
 * 
 * @param {function} onClick - Click handler for the toggle.
 *  
 * @returns {ReactNode} - SwitcherToggle.
 */
const SwitcherToggle = React.memo(({
    onClick,
    onWorkspaceSelect,
    onWorkspaceAdd,
    onPublishTemplate,
    onLogout,
    workspaces = [],
    activeWorkspaceKey = null,
}) => {

    // ----- CONTEXT ----- //

    // Theme Context
    const { theme } = useTheme();

    // Workspace Context
    const { activeWorkspace } = useWorkspace();

    // ----- STATE ----- //

    // Hover State
    const [hovered, setHovered] = useState(false);

    // Popup Open State
    const [popupOpen, setPopupOpen] = useState(false);

    // Invite Modal Open State
    const [inviteModalOpen, setInviteModalOpen] = useState(false);

    // Settings Modal Open State
    const [settingsModalOpen, setSettingsModalOpen] = useState(false);

    // Settings Modal Initial Tab State
    const [settingsInitialTab, setSettingsInitialTab] = useState(null);

    // Invite Modal Workspace
    const [inviteWorkspace, setInviteWorkspace] = useState(null);

    // ----- MEMOS ----- //

    // Active workspace fallback for panel-level actions.
    const panelWorkspace = useMemo(() => {
        if (activeWorkspace?.key) return activeWorkspace;
        if (!activeWorkspaceKey) return null;
        return workspaces.find((workspace) => workspace?.key === activeWorkspaceKey || workspace?.id === activeWorkspaceKey) || null;
    }, [activeWorkspace, activeWorkspaceKey, workspaces]);

    // ----- REFS ----- //

    // Anchor Ref for Popup Positioning
    const anchorRef = useRef(null);

    // ----- HANDLERS ----- //

    /**
     * Handles selecting a workspace from the popup.
     *
     * @param {object} workspace - The selected workspace object.
     */
    const handleWorkspaceSelect = (workspace) => {
        setPopupOpen(false);
        onWorkspaceSelect && onWorkspaceSelect(workspace);
    };

    /**
     * Opens settings and closes the popup.
     */
    const handleSettingsClick = (_workspace = null, nextTab = null) => {
        setPopupOpen(false);
        setSettingsInitialTab(nextTab || null);
        setSettingsModalOpen(true);
    };

    /**
     * Opens billing plan settings and closes the popup.
     */
    const handlePlanClick = () => {
        handleSettingsClick(null, SETTINGS_TAB_PLAN);
    };

    /**
     * Opens invite modal for the selected workspace.
     *
     * @param {object} workspace - Optional workspace override.
     */
    const handleInviteClick = (workspace) => {
        const nextWorkspace = workspace || panelWorkspace || null;
        if (!nextWorkspace?.key) return;

        setPopupOpen(false);
        setInviteWorkspace(nextWorkspace);
        setInviteModalOpen(true);
    };

    /**
     * Closes the settings modal.
     */
    const handleSettingsModalClose = () => {
        setSettingsModalOpen(false);
        setSettingsInitialTab(null);
    };

    /**
     * Closes the invite modal and clears selected workspace.
     */
    const handleInviteModalClose = () => {
        setInviteModalOpen(false);
        setInviteWorkspace(null);
    };

    /**
     * Toggles the workspace popup and notifies the parent click handler.
     */
    const handleToggleClick = () => {
        setPopupOpen((prev) => !prev);
        if (onClick) onClick();
    };

    // ----- RENDER ----- //

    return (
        <>

            {/* TOGGLE CONTAINER */}
            <div
                className="switcher-toggle-container"
                style={{
                    backgroundColor: hovered ? theme.buttonHoverBackgroundColor : 'transparent',
                }}
                ref={anchorRef}
                onClick={handleToggleClick}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
            >

                {/* SPACE PHOTO */}
                <div className="switcher-toggle-photo">

                    <WorkspaceCover
                        workspace={activeWorkspace}
                        borderWidth={0}
                        borderRadius={4}
                        size={42}
                        shape={PhotoShape.SQUARE}
                    />

                </div>


                {/* LABEL AND TITLE */}
                <div className="switcher-toggle-text-wrapper">

                    {/* TEXT */}
                    <div
                        className="switcher-toggle-label"
                        style={{
                            color: hovered ? theme.buttonHoverLabelColor : theme.foregroundMuted,
                        }}
                    >
                        SPACE
                    </div>

                    {/* TITLE */}
                    <div className="switcher-toggle-title-wrapper">

                        {/* TEXT */}
                        <div
                            className="switcher-toggle-title"
                            style={{
                                color: hovered ? theme.buttonHoverLabelColor : theme.foreground,
                            }}
                        >
                            {activeWorkspace?.title || ''}
                        </div>

                    </div>

                </div>

                {/* ARROW */}
                <div className="switcher-toggle-arrow">
                    <Icon
                        iconKey="arrow_drop_down"
                        size={16}
                        color={theme.foreground}
                    />
                </div>

            </div>

            {/* POPUP */}
            <Popup anchorRef={anchorRef} isOpen={popupOpen} setOpen={setPopupOpen}>
                <SwitcherPopup
                    onWorkspaceSelect={handleWorkspaceSelect}
                    onWorkspaceAdd={onWorkspaceAdd}
                    onPublishTemplate={onPublishTemplate}
                    onPlanClick={handlePlanClick}
                    onSettingsClick={handleSettingsClick}
                    onInviteClick={handleInviteClick}
                    onLogout={onLogout}
                    workspaces={workspaces}
                    activeWorkspaceKey={activeWorkspaceKey}
                />
            </Popup>

            {/* INVITE MODAL */}
            <InviteModal
                isOpen={inviteModalOpen}
                setOpen={setInviteModalOpen}
                workspace={inviteWorkspace || panelWorkspace}
                onClose={handleInviteModalClose}
            />

            {/* SETTINGS MODAL */}
            <SettingsModal
                isOpen={settingsModalOpen}
                setOpen={setSettingsModalOpen}
                initialTab={settingsInitialTab}
                workspace={panelWorkspace}
                onInviteClick={handleInviteClick}
                onClose={handleSettingsModalClose}
            />
        </>
    );

});

// Dev Tools
SwitcherToggle.displayName = 'SwitcherToggle';

export default SwitcherToggle;

